/**
 * Custom hook for resolving inspection components.
 *
 * Wraps inspectionComponentResolver with React state management,
 * loading/error handling, and race condition prevention.
 *
 * Usage:
 * ```typescript
 * const { components, loading, error } = useInspectionComponents({
 *     buildingKey: 'abc123',
 *     building: currentBuilding,
 *     componentKey: check.componentKey
 * });
 * ```
 */

import { useState, useEffect } from 'react';
import { Building } from '../../types/protocols';
import { resolveInspectionComponents, ResolvedComponent } from '../../Pages/BuildingChecks/inspectionComponentResolver';
import { useBuildComponentTypes } from '../../contexts/BuildComponentTypesContext';
import type { AssessmentGroupKey } from '../../Helpers/assessmentGroups';

interface UseInspectionComponentsProps {
    buildingKey: string;
    building: Building | null;
    /** The check's component. Only used if baseTypeGroup is not set. */
    componentKey?: string;
    /** Template's assessment group (Skickbedömning). If set, resolves all live components in the group. */
    baseTypeGroup?: AssessmentGroupKey;
}

interface UseInspectionComponentsReturn {
    components: ResolvedComponent[];
    loading: boolean;
    error: string | null;
    /** Refetch components (useful after building data changes) */
    refresh: () => void;
}

export const useInspectionComponents = ({
    buildingKey,
    building,
    componentKey,
    baseTypeGroup
}: UseInspectionComponentsProps): UseInspectionComponentsReturn => {
    const { types } = useBuildComponentTypes();
    const [components, setComponents] = useState<ResolvedComponent[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        // Nothing to resolve for a building-wide check
        if (!baseTypeGroup && !componentKey) {
            setComponents([]);
            setLoading(false);
            return;
        }

        let cancelled = false;  // Prevent race conditions
        setLoading(true);
        setError(null);

        resolveInspectionComponents(buildingKey, building, componentKey, baseTypeGroup, types)
            .then(result => {
                if (!cancelled) {
                    setComponents(result);
                }
            })
            .catch(err => {
                console.error('Failed to load inspection components:', err);
                if (!cancelled) {
                    setError('Kunde inte ladda komponenter');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [buildingKey, building, componentKey, baseTypeGroup, types, refreshKey]);

    const refresh = () => {
        setRefreshKey(prev => prev + 1);
    };

    return { components, loading, error, refresh };
};
